import { $availableVersions, $currentVersionId, $guests, $customFields, versionActions } from './state';
import { storageManager } from './storage';
import { showNotification } from './notifications';
import { DialogHelper } from './dialog';
import { historyNavigation } from './history';

const dialog = new DialogHelper();

// Version switching functionality
export async function switchToVersion(versionId: string): Promise<void> {
  try {
    const version = await storageManager.getVersion(versionId);
    if (!version) {
      throw new Error('Version not found');
    }

    // Update current version
    versionActions.setCurrentVersion(versionId);
    
    // Load version state
    $guests.set(version.state.guests);
    $customFields.set(version.state.customFields);
    
    // Load version history
    historyNavigation.loadVersionHistory(version.history || []);
    
    // Set as last used
    await storageManager.setLastUsedVersion(versionId);
    
    showNotification('Version Switched', `Switched to "${version.name}"`, 'success');
  } catch (error) {
    console.error('Failed to switch version:', error);
    throw error;
  }
}

// Dialog functions for version management
export function showRenameVersionDialog(versionId: string): void {
  const versions = $availableVersions.get();
  const version = versions.find(v => v.id === versionId);
  if (!version) return;

  // Clone template and populate with data
  const template = document.getElementById('rename-version-template') as HTMLTemplateElement;
  const content = template.content.cloneNode(true) as DocumentFragment;
  const input = content.querySelector('#versionName') as HTMLInputElement;
  input.value = version.name;

  dialog.show({
    title: 'Rename Version',
    content: content.firstElementChild as HTMLElement,
    className: 'edit-version-dialog'
  }, [
    {
      text: 'Cancel',
      className: 'modal-btn',
      handler: () => dialog.close()
    },
    {
      text: 'Rename',
      className: 'modal-btn modal-btn-primary',
      primary: true,
      handler: async () => {
        const input = document.getElementById('versionName') as HTMLInputElement;
        const newName = input.value.trim();
        
        if (!newName) {
          showNotification('Error', 'Version name cannot be empty', 'error');
          return;
        }

        // Check for duplicate names
        const isDuplicate = await storageManager.isVersionNameTaken(newName, versionId);
        if (isDuplicate) {
          showNotification('Error', 'A version with this name already exists', 'error');
          return;
        }

        try {
          const updatedVersion = await storageManager.renameVersion(versionId, newName);
          versionActions.updateVersion(updatedVersion);
          showNotification('Success', `Version renamed to "${newName}"`, 'success');
          dialog.close();
        } catch (error) {
          console.error('Failed to rename version:', error);
          showNotification('Error', 'Failed to rename version', 'error');
        }
      }
    }
  ]);
}

export function showForkVersionDialog(versionId: string): void {
  const versions = $availableVersions.get();
  const version = versions.find(v => v.id === versionId);
  if (!version) return;

  const defaultName = `Copy of ${version.name}`;
  
  // Clone template and populate with data
  const template = document.getElementById('fork-version-template') as HTMLTemplateElement;
  const content = template.content.cloneNode(true) as DocumentFragment;
  const input = content.querySelector('#forkVersionName') as HTMLInputElement;
  input.value = defaultName;

  dialog.show({
    title: 'Fork Version',
    content: content.firstElementChild as HTMLElement,
    className: 'fork-version-dialog'
  }, [
    {
      text: 'Cancel',
      className: 'modal-btn',
      handler: () => dialog.close()
    },
    {
      text: 'Create Copy',
      className: 'modal-btn modal-btn-primary',
      primary: true,
      handler: async () => {
        const input = document.getElementById('forkVersionName') as HTMLInputElement;
        const newName = input.value.trim();
        
        if (!newName) {
          showNotification('Error', 'Version name cannot be empty', 'error');
          return;
        }

        // Check for duplicate names
        const isDuplicate = await storageManager.isVersionNameTaken(newName);
        if (isDuplicate) {
          showNotification('Error', 'A version with this name already exists', 'error');
          return;
        }

        try {
          const currentGuests = $guests.get();
          const currentCustomFields = $customFields.get();
          
          const newVersion = await storageManager.createVersion(
            newName,
            { guests: currentGuests, customFields: currentCustomFields }
          );
          
          versionActions.addVersion(newVersion);
          
          // Switch to the new version
          await switchToVersion(newVersion.id);
          
          showNotification('Success', `Created "${newName}"`, 'success');
          dialog.close();
        } catch (error) {
          console.error('Failed to fork version:', error);
          showNotification('Error', 'Failed to create copy', 'error');
        }
      }
    }
  ]);
}

export function showDeleteVersionDialog(versionId: string): void {
  const versions = $availableVersions.get();
  const version = versions.find(v => v.id === versionId);
  if (!version) return;

  // Don't allow deleting the last version
  if (versions.length <= 1) {
    showNotification('Error', 'Cannot delete the last remaining version', 'warning');
    return;
  }

  // Clone template and populate with data
  const template = document.getElementById('delete-version-template') as HTMLTemplateElement;
  const content = template.content.cloneNode(true) as DocumentFragment;
  const placeholder = content.querySelector('.version-name-placeholder') as HTMLElement;
  placeholder.textContent = version.name;

  dialog.show({
    title: 'Delete Version',
    content: content.firstElementChild as HTMLElement,
    className: 'delete-version-dialog'
  }, [
    {
      text: 'Cancel',
      className: 'modal-btn',
      handler: () => dialog.close()
    },
    {
      text: 'Delete',
      className: 'modal-btn',
      handler: async () => {
        try {
          await storageManager.deleteVersion(versionId);
          versionActions.removeVersion(versionId);
          
          // If we just deleted the current version, switch to another one
          if (versionId === $currentVersionId.get()) {
            const remainingVersions = $availableVersions.get();
            if (remainingVersions.length > 0) {
              await switchToVersion(remainingVersions[0].id);
            } else {
              // This shouldn't happen since we check for last version above
              versionActions.setCurrentVersion(null);
            }
          }
          
          showNotification('Success', `Deleted "${version.name}"`, 'success');
          dialog.close();
        } catch (error) {
          console.error('Failed to delete version:', error);
          showNotification('Error', 'Failed to delete version', 'error');
        }
      }
    }
  ]);
}